import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import './MultiSelectSearchable.css';

type Option = { value: string; label: string };

function normalizeForSearch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

type Props = {
  label: string;
  name: string;
  value: number[];
  options: Option[];
  onChange: (selected: number[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  error?: string;
  className?: string;
  /** Menú flotante (portal) para modales y evitar scroll anidado. */
  menuPortal?: boolean;
  maxSelections?: number;
  onMaxSelections?: () => void;
  preserveCase?: boolean;
  sortAlphabetically?: boolean;
  selectedFirst?: boolean;
};

const MultiSelectSearchable: React.FC<Props> = ({
  label,
  name,
  value = [],
  options,
  onChange,
  placeholder = 'Buscar...',
  searchPlaceholder = 'Buscar...',
  error,
  className = '',
  menuPortal = false,
  maxSelections,
  onMaxSelections,
  preserveCase = false,
  sortAlphabetically = false,
  selectedFirst = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [listMaxHeight, setListMaxHeight] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const fmt = (s: string) => (preserveCase ? s : s.toUpperCase());

  const normalizedValue = useMemo(
    () => value.map((v) => Number(v)).filter((v) => !isNaN(v)),
    [value]
  );

  const orderedOptions = useMemo(() => {
    let list = [...options];
    if (sortAlphabetically) {
      list.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    }
    if (selectedFirst && normalizedValue.length > 0) {
      const selectedSet = new Set(normalizedValue);
      const selected = list.filter((o) => selectedSet.has(Number(o.value)));
      const rest = list.filter((o) => !selectedSet.has(Number(o.value)));
      list = [...selected, ...rest];
    }
    return list;
  }, [options, sortAlphabetically, selectedFirst, normalizedValue]);

  const filteredOptions = useMemo(() => {
    const q = normalizeForSearch(searchTerm.trim());
    if (!q) return orderedOptions;
    return orderedOptions.filter((option) =>
      normalizeForSearch(option.label).includes(q)
    );
  }, [orderedOptions, searchTerm]);

  const positionMenu = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const maxList = 320;
    const searchReserve = 52;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const preferBelow = spaceBelow >= 140 || spaceBelow >= spaceAbove;
    const raw = Math.min(maxList, preferBelow ? spaceBelow : spaceAbove);
    const maxH = Math.max(120, raw - searchReserve);
    setListMaxHeight(maxH);
    setMenuStyle({
      position: 'fixed',
      left: r.left,
      width: Math.max(r.width, 280),
      zIndex: 12000,
      ...(preferBelow
        ? { top: r.bottom + 6, bottom: 'auto' }
        : { top: 'auto', bottom: window.innerHeight - r.top + 6 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !menuPortal) return;
    positionMenu();
    const onReflow = () => positionMenu();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [isOpen, menuPortal, positionMenu, filteredOptions.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearchTerm('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (optionValue: string) => {
    const numValue = Number(optionValue);
    if (normalizedValue.includes(numValue)) {
      onChange(normalizedValue.filter((v) => v !== numValue));
      return;
    }
    if (maxSelections != null && normalizedValue.length >= maxSelections) {
      onMaxSelections?.();
      return;
    }
    onChange([...normalizedValue, numValue]);
  };

  const selectedLabels = options
    .filter((opt) => normalizedValue.includes(Number(opt.value)))
    .map((opt) => opt.label);

  const rootClass = [
    'multi-select-searchable',
    className,
    menuPortal ? 'multi-select-searchable--portal' : '',
    isOpen ? 'multi-select-searchable--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const menuContent = (
    <div
      ref={menuRef}
      className={`multi-select-menu ${menuPortal ? 'multi-select-menu--portal' : ''}`}
      style={menuPortal ? menuStyle : undefined}
      role="listbox"
      aria-multiselectable="true"
    >
      <div className="search-container">
        <Search className="search-container__icon" size={18} aria-hidden />
        <input
          type="text"
          className="search-input"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      </div>
      <div
        className="options-container"
        style={menuPortal && listMaxHeight ? { maxHeight: listMaxHeight } : undefined}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = normalizedValue.includes(Number(option.value));
            const atMax =
              maxSelections != null &&
              !isSelected &&
              normalizedValue.length >= maxSelections;
            return (
              <label
                key={option.value}
                className={`option-item ${isSelected ? 'selected' : ''} ${
                  atMax ? 'option-item--disabled' : ''
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={atMax}
                  onChange={() => handleToggle(option.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span
                  className={`option-label ${
                    preserveCase ? 'option-label--preserve' : ''
                  }`}
                >
                  {fmt(option.label)}
                </span>
              </label>
            );
          })
        ) : (
          <div className="no-results">No se encontraron resultados</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={rootClass} ref={containerRef}>
      <div className="form-group">
        <div className="multi-select-label-row">
          <label htmlFor={name}>{label}</label>
          {maxSelections != null && (
            <span className="multi-select-limit" aria-live="polite">
              {normalizedValue.length}/{maxSelections}
            </span>
          )}
        </div>
        <button
          ref={triggerRef}
          type="button"
          id={name}
          className={`multi-select-dropdown ${isOpen ? 'open' : ''} ${error ? 'error' : ''}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <div className="multi-select-display">
            {selectedLabels.length > 0 ? (
              <div className="selected-items">
                {selectedLabels.slice(0, 2).map((lbl, idx) => (
                  <span key={idx} className="selected-tag">
                    {fmt(lbl)}
                  </span>
                ))}
                {selectedLabels.length > 2 && (
                  <span className="selected-count">
                    +{selectedLabels.length - 2} más
                  </span>
                )}
              </div>
            ) : (
              <span className="placeholder-text">{placeholder}</span>
            )}
            <ChevronDown
              className="dropdown-chevron"
              size={22}
              strokeWidth={2.25}
              aria-hidden
            />
          </div>
        </button>

        {isOpen &&
          (menuPortal
            ? createPortal(menuContent, document.body)
            : menuContent)}

        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectSearchable;
